import { useState } from 'preact/hooks';
import { inStockholm, kr, travelInvoiced, travelLines, travelSummary, type BasisLine, type TravelIncluded, type TravelRule } from '@kff/domain';
import type { Api } from '../api.js';
import { dm, dmSv } from '../format.js';
import { texts, useLang } from '../i18n.js';
import { Dialog, toast } from '../ui.js';

/**
 * Travel compensation (owner 2026-10-29, thread "Hantera reseersättning per kund"): inside Stockholm travel is in the
 * price; outside, the client pays the actual cost plus the travel time at half the hourly price. The client card shows the
 * rule and lets someone with clients.edit set the client's own; the month's basis gets the trips as lines, with a nudge for
 * shoots outside Stockholm that have no trip yet.
 */

export interface TravelHint {
  shootId: string;
  projectId: string;
  project: string;
  date: string;
  place: string;
}
export interface TravelView {
  rule: TravelRule;
  standard: boolean;
  rate: number;
  hourly: number;
  source: 'client' | 'agreement' | 'standard';
  agreementRef: string | null;
  base: TravelRule;
  /** the client's own rule differs from the agreement in force */
  differs?: boolean;
  summary: string;
  hints: TravelHint[];
}

const TT = texts(
  {
    head: 'Travel',
    own: 'Adjusted for this client',
    fromAgreement: (r: string) => `From agreement ${r}`,
    differs: (r: string) => `Differs from agreement ${r}: the client's own rule wins. Go back to the agreement if its terms should apply.`,
    standard: 'Standard (no agreement in the Hub)',
    change: 'Change',
    title: 'Travel for this client',
    sub: 'Standard: travel inside Stockholm is in the price; outside, the actual cost plus the travel time at half the hourly price.',
    included: 'Travel included in the price',
    inc: { stockholm: 'Inside Stockholm (standard)', all: 'All travel, wherever', none: 'None: all travel is invoiced' } as Record<TravelIncluded, string>,
    share: 'Travel time, share of the hourly price (%)',
    hourly: 'Hourly price (kr/h)',
    hourlyHint: (n: string) => `Empty = the price list's ${n} kr/h.`,
    note: 'What was agreed (optional)',
    preview: 'Becomes:',
    save: 'Save',
    cancel: 'Cancel',
    reset: 'Back to the standard',
    resetAgreement: 'Back to the agreement',
    saved: 'Travel rule saved',
    // a trip on the basis
    addTrip: 'Add travel',
    tripTitle: 'Add travel',
    tripSub: (s: string) => s,
    place: 'Where',
    date: 'Date',
    cost: 'Travel cost excl. VAT (kr)',
    costHint: 'Tickets, mileage, hotel, parking: at cost.',
    hours: 'Travel time (hours)',
    hoursHint: 'Door to door, all of the crew together.',
    lines: 'Lines on the basis',
    add: 'Add to the basis',
    inside: 'This looks like inside Stockholm, where travel is in the price.',
    allIncluded: 'Travel is included in the price for this client.',
    nothing: 'Enter a cost or travel time.',
    hintsHead: 'Shoots outside Stockholm this month',
    hintAdd: 'Add travel',
    added: 'Added. Save or confirm the basis to keep it.'
  },
  {
    head: 'Reseersättning',
    own: 'Justerad för kunden',
    fromAgreement: (r: string) => `Från avtal ${r}`,
    differs: (r: string) => `Avviker från avtal ${r}: kundens egen regel gäller. Gå tillbaka till avtalet om dess villkor ska gälla.`,
    standard: 'Standard (inget avtal i Hubben)',
    change: 'Ändra',
    title: 'Reseersättning för kunden',
    sub: 'Standard: resor inom Stockholm ingår i priset; utanför debiteras faktisk resekostnad plus restid till halva timpriset.',
    included: 'Resor som ingår i priset',
    inc: { stockholm: 'Inom Stockholm (standard)', all: 'Alla resor, oavsett var', none: 'Inga: alla resor debiteras' },
    share: 'Restid, andel av timpriset (%)',
    hourly: 'Timpris (kr/h)',
    hourlyHint: (n: string) => `Tomt = prislistans ${n} kr/h.`,
    note: 'Vad som är överenskommet (valfritt)',
    preview: 'Blir:',
    save: 'Spara',
    cancel: 'Avbryt',
    reset: 'Tillbaka till standard',
    resetAgreement: 'Tillbaka till avtalet',
    saved: 'Reseregeln är sparad',
    addTrip: 'Lägg till resa',
    tripTitle: 'Lägg till resa',
    tripSub: (s: string) => s,
    place: 'Vart',
    date: 'Datum',
    cost: 'Resekostnad exkl. moms (kr)',
    costHint: 'Biljetter, milersättning, hotell, parkering: till självkostnad.',
    hours: 'Restid (timmar)',
    hoursHint: 'Dörr till dörr, hela teamet tillsammans.',
    lines: 'Rader i underlaget',
    add: 'Lägg till i underlaget',
    inside: 'Det här ser ut att vara inom Stockholm, där resor ingår i priset.',
    allIncluded: 'Resor ingår i priset för den här kunden.',
    nothing: 'Fyll i en kostnad eller restid.',
    hintsHead: 'Inspelningar utanför Stockholm denna månad',
    hintAdd: 'Lägg till resa',
    added: 'Tillagt. Spara eller bekräfta underlaget för att behålla det.'
  }
);

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const num = (s: string) => (s.trim() === '' ? 0 : Number(s.replace(',', '.')));

/** The client card's travel row: the rule in one sentence, and Change for clients.edit. */
export function TravelRow<V extends { travel: TravelView }>(p: { api: Api; clientId: string; v: V; canEdit: boolean; onSaved: (v: V) => void }) {
  const lang = useLang();
  const t = TT[lang];
  const [open, setOpen] = useState(false);
  const tr = p.v.travel;
  return (
    <div class="bill-travel">
      <p class="small">
        <strong>{t.head}</strong> <span class="muted">· {tr.source === 'client' ? t.own : tr.source === 'agreement' && tr.agreementRef ? t.fromAgreement(tr.agreementRef) : t.standard}</span>
        {p.canEdit ? (
          <>
            {' '}
            <button type="button" class="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>
              {t.change}
            </button>
          </>
        ) : null}
      </p>
      <p class="small">{tr.summary}</p>
      {tr.differs && tr.agreementRef ? <p class="small warn-text">{t.differs(tr.agreementRef)}</p> : null}
      {tr.rule.note ? <p class="small muted">{tr.rule.note}</p> : null}
      {open ? <TravelRuleDialog api={p.api} clientId={p.clientId} travel={tr} onSaved={p.onSaved as (v: unknown) => void} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

function TravelRuleDialog(p: { api: Api; clientId: string; travel: TravelView; onSaved: (v: unknown) => void; onClose: () => void }) {
  const lang = useLang();
  const t = TT[lang];
  const [included, setIncluded] = useState<TravelIncluded>(p.travel.rule.included);
  const [share, setShare] = useState(String(Math.round(p.travel.rule.timeShare * 100)));
  const [hourly, setHourly] = useState(p.travel.rule.hourlyRate === null ? '' : String(p.travel.rule.hourlyRate));
  const [note, setNote] = useState(p.travel.rule.note ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // the price list's hourly price: what the share is of when no own price is set
  const base = p.travel.hourly;
  const rule: TravelRule = { included, timeShare: Math.min(1, Math.max(0, num(share) / 100)), hourlyRate: hourly.trim() ? num(hourly) : null, note: note.trim() || null };
  const save = async (body: { rule: TravelRule | null }) => {
    setBusy(true);
    setErr(null);
    try {
      p.onSaved(await p.api.put(`/api/clients/${p.clientId}/travel`, body));
      toast({ text: t.saved });
      p.onClose();
    } catch (e) {
      setErr(msg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t.title}
      sub={t.sub}
      onClose={p.onClose}
      onSubmit={() => void save({ rule })}
      foot={
        <>
          {p.travel.source === 'client' ? (
            <button type="button" class="btn btn-ghost" disabled={busy} onClick={() => void save({ rule: null })}>
              {p.travel.agreementRef ? t.resetAgreement : t.reset}
            </button>
          ) : null}
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="submit" class="btn btn-primary" disabled={busy}>
            {t.save}
          </button>
        </>
      }
    >
      <fieldset class="field">
        <legend class="label">{t.included}</legend>
        {(['stockholm', 'all', 'none'] as TravelIncluded[]).map((k) => (
          <label class="check" key={k}>
            <input type="radio" name="travel-inc" checked={included === k} onChange={() => setIncluded(k)} /> {t.inc[k]}
          </label>
        ))}
      </fieldset>
      {included !== 'all' ? (
        <div class="cluster">
          <div class="field">
            <label class="label" for="travel-share">
              {t.share}
            </label>
            <input class="input input--num" id="travel-share" type="number" min="0" max="100" step="5" value={share} onInput={(e) => setShare((e.target as HTMLInputElement).value)} />
          </div>
          <div class="field">
            <label class="label" for="travel-hourly">
              {t.hourly}
            </label>
            <input class="input input--num" id="travel-hourly" type="number" min="0" step="50" placeholder={String(base)} value={hourly} onInput={(e) => setHourly((e.target as HTMLInputElement).value)} />
            <p class="hint">{t.hourlyHint(base.toLocaleString(lang === 'sv' ? 'sv-SE' : 'en-GB'))}</p>
          </div>
        </div>
      ) : null}
      <div class="field">
        <label class="label" for="travel-note">
          {t.note}
        </label>
        <input class="input input--wide" id="travel-note" maxLength={500} value={note} onInput={(e) => setNote((e.target as HTMLInputElement).value)} />
      </div>
      <p class="small">
        <span class="muted">{t.preview}</span> {travelSummary(rule, base, lang)}
      </p>
      {err ? (
        <p class="field-error" role="alert">
          {err}
        </p>
      ) : null}
    </Dialog>
  );
}

/** A trip as lines on the basis: where, when, the cost and the travel time, with the lines it becomes shown before adding. */
export function TripDialog(p: { travel: TravelView; hint?: TravelHint | null; onAdd: (lines: BasisLine[]) => void; onClose: () => void }) {
  const lang = useLang();
  const t = TT[lang];
  const [place, setPlace] = useState(p.hint?.place ?? '');
  const [date, setDate] = useState(p.hint?.date ?? '');
  const [cost, setCost] = useState('');
  const [hours, setHours] = useState('');
  const r = p.travel.rule;
  const lines = travelLines(r, p.travel.hourly, { place, date: date || null, cost: num(cost), hours: num(hours), projectId: p.hint?.projectId ?? null, shootId: p.hint?.shootId ?? null });
  const warn = r.included === 'all' ? t.allIncluded : !travelInvoiced(r, place) && inStockholm(place) ? t.inside : null;
  const add = () => {
    if (!lines.length) return;
    p.onAdd(lines);
    toast({ text: t.added });
    p.onClose();
  };
  return (
    <Dialog
      title={t.tripTitle}
      sub={p.hint ? `${p.hint.project}` : t.tripSub(p.travel.summary)}
      onClose={p.onClose}
      onSubmit={add}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="submit" class="btn btn-primary" disabled={!lines.length}>
            {t.add}
          </button>
        </>
      }
    >
      <div class="cluster">
        <div class="field">
          <label class="label" for="trip-place">
            {t.place}
          </label>
          <input class="input input--wide" id="trip-place" maxLength={120} value={place} onInput={(e) => setPlace((e.target as HTMLInputElement).value)} />
        </div>
        <div class="field">
          <label class="label" for="trip-date">
            {t.date}
          </label>
          <input class="input" id="trip-date" type="date" value={date} onInput={(e) => setDate((e.target as HTMLInputElement).value)} />
        </div>
      </div>
      <div class="cluster">
        <div class="field">
          <label class="label" for="trip-cost">
            {t.cost}
          </label>
          <input class="input input--num" id="trip-cost" type="number" min="0" step="1" value={cost} onInput={(e) => setCost((e.target as HTMLInputElement).value)} />
          <p class="hint">{t.costHint}</p>
        </div>
        <div class="field">
          <label class="label" for="trip-hours">
            {t.hours}
          </label>
          <input class="input input--num" id="trip-hours" type="number" min="0" step="0.5" value={hours} onInput={(e) => setHours((e.target as HTMLInputElement).value)} />
          <p class="hint">{t.hoursHint}</p>
        </div>
      </div>
      {warn ? <p class="small contract-warn">{warn}</p> : null}
      <p class="small muted">{p.travel.summary}</p>
      {lines.length ? (
        <>
          <p class="label">{t.lines}</p>
          <ul class="small">
            {lines.map((l) => (
              <li key={l.text}>
                {l.text}: {l.unit === 'h' ? `${String(l.qty).replace('.', ',')} h × ${kr(l.unitPrice ?? 0)} = ` : ''}
                <strong>{kr(Math.round(l.qty * (l.unitPrice ?? 0)))}</strong>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p class="small muted">{t.nothing}</p>
      )}
    </Dialog>
  );
}

/** Shoots outside Stockholm this month with no trip on the basis yet, each with a button that fills in the trip. */
export function TravelHints(p: { hints: TravelHint[]; lines: BasisLine[]; onPick: (h: TravelHint) => void }) {
  const lang = useLang();
  const t = TT[lang];
  const taken = new Set(p.lines.flatMap((l) => (l.shootId ? [l.shootId] : [])));
  const open = p.hints.filter((h) => !taken.has(h.shootId));
  if (!open.length) return null;
  return (
    <div class="bill-hints">
      <p class="small">
        <strong>{t.hintsHead}</strong>
      </p>
      <ul class="small">
        {open.map((h) => (
          <li key={h.shootId}>
            {h.place} · {lang === 'sv' ? dmSv(h.date) : dm(h.date)} · <span class="muted">{h.project}</span>{' '}
            <button type="button" class="btn btn-sm" onClick={() => p.onPick(h)}>
              {t.hintAdd}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const tripLabel = (lang: 'sv' | 'en') => TT[lang].addTrip;
